import { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { SupportRequestAddItem } from '../../utils/types/supportTypes'
import { supportRequestInit } from '../../utils/initialisations/supportInit'
import useAddSupportRequest from '../../hooks/support/useAddSupportRequest'
import useFetchSupportReasons from '../../hooks/support/useFetchSupportReasons'
import DropdownLoadingMessage from '../default/DropdownLoadingMessage'
import { useAuthContext } from '../../context/auth/AuthContextProvider'
import SupportImagePicker from './SupportImagePicker'

type SupportRequestPopupProps = {
  openPopup: boolean
  setOpenPopup: (value: boolean) => void
  navWidth: number
}

const SupportRequestPopup = ({
  openPopup,
  setOpenPopup,
  navWidth,
}: SupportRequestPopupProps) => {
  const { user } = useAuthContext()
  const { isPending: isSubmitting, handleAddSupportRequest } =
    useAddSupportRequest()
  const { supportReasons, isPending, hasError } =
    useFetchSupportReasons(openPopup)

  const [supportRequest, setSupportRequest] =
    useState<SupportRequestAddItem>(supportRequestInit)

  const handleClosePopup = () => {
    setSupportRequest(supportRequestInit)
    setOpenPopup(false)
  }

  return (
    <Dialog
      open={openPopup}
      onClose={handleClosePopup}
      fullWidth
      disableScrollLock
      sx={{
        '& .MuiDialog-container': {
          pl: `${navWidth}px`,
          transition: 'padding-left var(--global-transition-timing)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Contact Support</DialogTitle>

      <DialogContent>
        <Box mt={2}>
          <FormControl fullWidth>
            <InputLabel id="support-reason-label" size="small" required>
              Reason
            </InputLabel>

            <Select
              name="reason"
              labelId="support-reason-label"
              size="small"
              required
              value={supportRequest.reason}
              onChange={(event: SelectChangeEvent<string>) =>
                setSupportRequest((prev) => ({
                  ...prev,
                  reason: event.target.value,
                }))
              }
              input={<OutlinedInput id="support-reason" label="Reason" />}
              sx={{ textAlign: 'left' }}
            >
              {isPending && <DropdownLoadingMessage />}

              {!isPending && supportReasons.length === 0 && (
                <MenuItem value="" disabled>
                  {hasError
                    ? 'Could not load the reasons'
                    : 'No reasons available'}
                </MenuItem>
              )}

              {supportReasons.map((supportReason) => (
                <MenuItem key={supportReason.value} value={supportReason.value}>
                  {supportReason.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mt={2}>
          <TextField
            name="subject"
            label="Subject"
            value={supportRequest.subject}
            size="small"
            fullWidth
            required
            onChange={(e) => {
              const inputValue = e.target.value
              setSupportRequest((prev) => ({
                ...prev,
                subject: inputValue,
              }))
            }}
          />
        </Box>

        <Box mt={2}>
          <TextField
            name="body"
            label="Details"
            value={supportRequest.body}
            size="small"
            fullWidth
            multiline
            rows={6}
            required
            onChange={(e) => {
              const inputValue = e.target.value
              setSupportRequest((prev) => ({
                ...prev,
                body: inputValue,
              }))
            }}
          />
        </Box>

        <Box mt={2}>
          <SupportImagePicker
            images={supportRequest.images}
            setImages={(images) =>
              setSupportRequest((prev) => ({ ...prev, images }))
            }
            disabled={isSubmitting}
          />
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="sharedWithOrganisation"
                checked={supportRequest.sharedWithOrganisation}
                onChange={(e) =>
                  setSupportRequest((prev) => ({
                    ...prev,
                    sharedWithOrganisation: e.target.checked,
                  }))
                }
              />
            }
            label={`Let my colleagues at ${user.organisationName} see this request and its replies`}
            slotProps={{ typography: { fontSize: '0.875rem' } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ mr: 3, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={handleClosePopup}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={isSubmitting}
          onClick={() =>
            handleAddSupportRequest({
              supportRequest,
              fnSuccess: handleClosePopup,
            })
          }
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SupportRequestPopup
